import React, { useState } from 'react';
import styled from 'styled-components';
import { useDesktop } from '../../contexts/DesktopContext';

const WallpaperManagerContainer = styled.div`
  padding: 20px;
  background: rgba(0, 0, 0, 0.9);
  color: #00ff00;
  font-family: 'Ubuntu', sans-serif;
  height: 100%;
  overflow-y: auto;
`;

const Title = styled.h2`
  color: #00ffff;
  margin-bottom: 20px;
  text-align: center;
`;

const WallpaperGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const WallpaperOption = styled.div<{ isSelected: boolean }>`
  position: relative;
  height: 120px;
  border-radius: 8px;
  cursor: pointer;
  border: 3px solid ${props => props.isSelected ? '#00ff00' : 'rgba(0, 255, 0, 0.3)'};
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: #00ff00;
    transform: scale(1.05);
  }
`;

const WallpaperPreview = styled.div<{ wallpaper: string }>`
  width: 100%;
  height: 100%;
  background: ${props => getWallpaperStyle(props.wallpaper)};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00ff00;
  font-weight: bold;
`;

const WallpaperName = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  color: #00ff00;
  padding: 5px;
  text-align: center;
  font-size: 12px;
`;

const ApplyButton = styled.button`
  background: rgba(0, 255, 0, 0.2);
  border: 1px solid #00ff00;
  color: #00ff00;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  width: 100%;
  margin-top: 20px;

  &:hover {
    background: rgba(0, 255, 0, 0.3);
  }
`;

const getWallpaperStyle = (wallpaper: string): string => {
  const wallpapers: Record<string, string> = {
    cybersecurity: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)',
    matrix: 'linear-gradient(135deg, #000000 0%, #001100 50%, #003300 100%)',
    hacker: 'linear-gradient(135deg, #0f0f0f 0%, #1a0033 50%, #330066 100%)',
    terminal: 'linear-gradient(135deg, #000000 0%, #1a1a00 50%, #333300 100%)',
    neon: 'linear-gradient(135deg, #ff00ff 0%, #00ffff 50%, #ffff00 100%)',
    dark: 'linear-gradient(135deg, #000000 0%, #111111 50%, #222222 100%)',
    blue: 'linear-gradient(135deg, #001122 0%, #003366 50%, #0066cc 100%)',
    red: 'linear-gradient(135deg, #220011 0%, #660033 50%, #cc0066 100%)'
  };
  return wallpapers[wallpaper] || wallpapers.cybersecurity;
};

const WallpaperManager: React.FC = () => {
  const { wallpaper, setWallpaper } = useDesktop();
  const [selectedWallpaper, setSelectedWallpaper] = useState(wallpaper);

  const wallpapers = [
    { id: 'cybersecurity', name: 'Cybersecurity' },
    { id: 'matrix', name: 'Matrix' },
    { id: 'hacker', name: 'Hacker' },
    { id: 'terminal', name: 'Terminal' },
    { id: 'neon', name: 'Neon' },
    { id: 'dark', name: 'Dark' },
    { id: 'blue', name: 'Blue' },
    { id: 'red', name: 'Red' }
  ];

  const handleApply = () => {
    setWallpaper(selectedWallpaper);
  };

  return (
    <WallpaperManagerContainer>
      <Title>🎨 Wallpaper Settings</Title>
      
      <WallpaperGrid>
        {wallpapers.map((wp) => (
          <WallpaperOption
            key={wp.id}
            isSelected={selectedWallpaper === wp.id}
            onClick={() => setSelectedWallpaper(wp.id)}
          >
            <WallpaperPreview wallpaper={wp.id}>
              {wp.name}
            </WallpaperPreview>
            <WallpaperName>{wp.name}</WallpaperName>
          </WallpaperOption>
        ))}
      </WallpaperGrid>

      <ApplyButton onClick={handleApply}>
        Apply Wallpaper
      </ApplyButton>
    </WallpaperManagerContainer>
  );
};

export default WallpaperManager;
